import React, { useState, useEffect } from "react";
import BCMSStateManagerCRUD from "../../classes/BCMSStateManagerCRUD";
import { useBCMSContext } from "../BCMSContext/main.jsx";
import BCMSRecord from "../../classes/BCMSRecord"

// Create context for managing the state of the StateManager
const StateManagerContext = React.createContext(null);
// (STATE, FN) -> React.Component
// Defines the main component
export default function BCMSStateManager(props) {
    // Retrieve the state and controller from the passed properties
    // Setting default values if nothing has been passed
    const values = props.state ?? {};
    const controller = props.controller ?? {};
    // Initalize a new state for the context
    const [ state, setState ] = useState(values);
    const context = {
        ...controller(state, setState),
        // Instance of BCMSStateManager, stores any ui instances to be added to the state
        uiConfig: BCMSStateManagerCRUD.init(state.ui),
        uiKey: 0,
        
        // getKey :: VOID -> INTEGER
        // Increment the uiKey within context and return the value
        getKey: () => {
            context.uiKey += 1;
            return context.uiKey;
        },
        // addUI :: (INTEGER|STRING, *) -> VOID
        // Add an update function to the uiConfig to update the state ui to include the values passed from properties
        addUI: (id, value) => context.uiConfig.update([ id ], value),    
        // setUI :: VOID -> VOID
        // Update the ui in the state to the result of the uiConfig combined with the existing state ui
        setUI: () => setState({ ...state, ui: { ...context.uiConfig.submit(), ...state.ui }})
    }

    // Whenever the state has been updated, check if there are any new 
    // operations in the uiConfig and run if there are.
    useEffect(() => {
        if(context.uiConfig.operations.length > 0) {
            context.setUI();
        }
    }, [ state ]);

    // Updated the state to the passed state from the properties
    useEffect(() => setState(values), [ props.state ]);

    return (
        <StateManagerContext.Provider value={ context }>
            { props.children }
        </StateManagerContext.Provider>
    );
}
// Hook to be able to retrieve the context of the state manager
export const useBCMSStateManager = () => React.useContext(StateManagerContext);

// useUIState :: (INTEGER|STRING, *) -> OBJECT
// Hook to create a new key and if the id doesn't exist within the ui already
// add the passed config property to the state ui. If the id does already exist
// within the ui then return the value that is set.
export const useUIState = (id, config) => {
    const { getKey, addUI, ui } = useBCMSStateManager();
    const [ key, setKey ] = useState(id ?? getKey());
    const isAdded = ui[key] !== undefined;

    if(!isAdded) { addUI(key, config); }
    return { key, config: !isAdded ? config : ui[key] };
};

// useStateWithContext :: [BCMSRecord] -> OBJECT
// Hook to return update the bcmsRecord classes with the context
// of the BCMSContext. Returns both the global context as well as the current
// page context
export const useStateWithContext = classes => {
    const global = useBCMSContext();
    const context = useBCMSStateManager();

    if(classes) { BCMSRecord.globalControllerFor(classes, global); }
    return { global, context }
};